// The event's course routes, analysed for this assessment (safety/routes.py):
// figures, time above each height for the leaders and the back of the field,
// the elevation profile, and the suggested forecast points — which the organiser
// ticks and adds, each with its own exposure window.
//
// The routes themselves are uploaded on the Event dialog (core app); only their
// analysis is beta. A route counts for this assessment when its start-to-close
// overlaps the assessment's window, which is what makes a multi-distance race
// and a stage race the same problem.
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip,
  CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { fmtMinutes, fmtRouteWindow, routeFigures } from "../eventRoutes";
import { notify } from "../notify";
import ElevationProfile from "./ElevationProfile";
import SafetySection from "./SafetySection";
import { SAFETY_API, fmtPointWindow } from "./format";

// Leaflet only matters to this one panel — its own chunk, like IncidentMap.
const SafetyRouteMap = lazy(() => import("./SafetyRouteMap"));

const key = (routeId, km) => `${routeId}:${km.toFixed(2)}`;
const onRoute = (points, routeId, km) =>
  points.find(p => p.route_id === routeId && p.distance_km !== null && Math.abs(p.distance_km - km) < 0.005);

export default function RoutesSection({ assessment, config, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const decided = assessment.status === "Decided";

  // Re-read whenever the assessment changes: its window decides which routes are
  // in focus, and its points decide which suggestions are already added.
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${SAFETY_API}/assessments/${assessment.id}/routes`)
      .then(res => setData(res.data.routes || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [assessment.id, assessment.window_start, assessment.window_end]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const inWindow = (data || []).filter(r => r.in_window);
  const others = (data || []).filter(r => !r.in_window);

  return (
    <SafetySection id="routes" title="Routes"
      subtitle={<>Courses uploaded on the event, with when participants are where. Timings come from each
        route's start and course close, and an expected winning time; add the suggested points
        to forecast each for the hours people are actually there.</>}
      summary={<RoutesSummary data={data} loading={loading} inWindow={inWindow} others={others} />}>
      {loading && !data ? <CircularProgress size={24} /> : data.length === 0 ? (
        <Alert severity="info">
          This event has no routes. Upload GPX files on the event (Events → the event → Routes) to
          analyse them here.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {inWindow.length === 0 && (
            <Alert severity="info">No route is open during this assessment's window.</Alert>
          )}
          {inWindow.length > 1 && (
            <Suspense fallback={<CircularProgress size={24} />}>
              <SafetyRouteMap routes={data} focusIds={inWindow.map(r => r.route.id)} points={assessment.points} />
            </Suspense>
          )}
          {inWindow.map(entry => (
            <RouteCard key={entry.route.id} entry={entry} assessment={assessment}
                       config={config} decided={decided} onChange={onChange} onReload={load}
                       showMap={inWindow.length === 1} allRoutes={data} />
          ))}
          {others.length > 0 && (
            <Accordion disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">
                  {others.length} other route{others.length === 1 ? "" : "s"} on this event, outside the window
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {others.map(o => (
                    <Box key={o.route.id}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.route.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {o.timed ? fmtRouteWindow(o.route) : "No start and course-close times set — set them on the event to include it."}
                        {" · "}{routeFigures(o.route)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      )}
    </SafetySection>
  );
}

// Collapsed: each route in the window with its key figures and time up high,
// no profile or map.
function RoutesSummary({ data, loading, inWindow, others }) {
  if (loading && !data) return <CircularProgress size={20} />;
  if (!data?.length) return <Typography variant="body2" color="text.secondary">No routes on this event.</Typography>;
  return (
    <Stack spacing={0.75}>
      {inWindow.length === 0 && (
        <Typography variant="body2" color="text.secondary">No route is open during this assessment's window.</Typography>
      )}
      {inWindow.map(({ route, analysis: an, error }) => (
        <Box key={route.id}>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600 }}>{route.name}</Box>
            {" · "}{fmtRouteWindow(route)} · {routeFigures(route)}
          </Typography>
          {error ? (
            <Typography variant="caption" color="error">{error}</Typography>
          ) : an?.bands?.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {an.bands.map(b => `${b.distance_km} km above ${b.threshold_m} m`
                + (b.leader_minutes != null ? ` (leaders ${fmtMinutes(b.leader_minutes)}, back ${fmtMinutes(b.back_minutes)})` : ""))
                .join(" · ")}
            </Typography>
          )}
        </Box>
      ))}
      {others.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          And {others.length} other route{others.length === 1 ? "" : "s"} outside the window.
        </Typography>
      )}
    </Stack>
  );
}

function RouteCard({ entry, assessment, config, decided, onChange, onReload, showMap, allRoutes }) {
  const { route, analysis: an, error } = entry;
  const suggestions = useMemo(() => an?.suggestions || [], [an]);
  const [chosen, setChosen] = useState(() => new Set());
  const [leader, setLeader] = useState("");
  const [manualKm, setManualKm] = useState("");
  const [busy, setBusy] = useState(false);

  // Tick every suggestion not yet added, each time the list changes — review
  // is then "untick what you don't want", which is the common case.
  useEffect(() => {
    setChosen(new Set(suggestions.filter(s => !onRoute(assessment.points, route.id, s.distance_km))
      .map(s => key(route.id, s.distance_km))));
  }, [suggestions, route.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLeader(an?.leader_hours_set ? String(an.leader_hours) : ""); }, [an]);

  if (error || !an) {
    return <Alert severity="error">{route.name}: {error || "couldn't be analysed."}</Alert>;
  }

  const toggle = (k) => setChosen(s => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const pending = suggestions.filter(s => chosen.has(key(route.id, s.distance_km))
                                       && !onRoute(assessment.points, route.id, s.distance_km));

  const add = (points) => {
    setBusy(true);
    axios.post(`${SAFETY_API}/assessments/${assessment.id}/points/from-route`, { route_id: route.id, points })
      .then(res => { onChange(res.data); notify(`Added ${points.length} point${points.length === 1 ? "" : "s"}.`, "success"); })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const saveLeader = () => {
    const v = leader.trim() === "" ? null : Number(leader);
    if (v !== null && !(v > 0)) return;
    if (v === (an.leader_hours_set ? an.leader_hours : null)) return;
    axios.put(`${SAFETY_API}/routes/${route.id}/settings`, { leader_hours: v })
      .then(() => {
        // Points' windows move with it; the assessment re-reads them.
        onReload();
        return axios.get(`${SAFETY_API}/assessments/${assessment.id}`);
      })
      .then(res => res && onChange(res.data))
      .catch(() => {});
  };

  const manual = Number(manualKm);
  const manualOk = manualKm.trim() !== "" && manual >= 0 && manual <= (route.distance_km || 0);
  const markers = suggestions.map((s, i) => ({
    key: key(route.id, s.distance_km), n: i + 1, km: s.distance_km, elevation: s.elevation_m, name: s.name,
    on: chosen.has(key(route.id, s.distance_km)), added: !!onRoute(assessment.points, route.id, s.distance_km),
  }));

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{route.name}</Typography>
      <Typography variant="body2" color="text.secondary">
        {fmtRouteWindow(route)} · {routeFigures(route)}
      </Typography>
      {!entry.timed && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This route has no start and course-close times, so nothing can be timed. Set them on the event.
        </Alert>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mt: 2 }}>
        <Box sx={{ minWidth: 260 }}>
          <TextField size="small" label="Expected winning time (hours)" type="number" value={leader}
                     onChange={(e) => setLeader(e.target.value)} onBlur={saveLeader}
                     onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                     placeholder={String(an.default_leader_hours)} disabled={decided}
                     slotProps={{ htmlInput: { min: 0.25, step: 0.25 } }}
                     helperText={an.leader_hours_set
                       ? "Shared by every assessment of this route."
                       : `Blank uses an estimate of ${an.default_leader_hours} h, from ${an.effort_km} effort-km (distance plus 1 km per 100 m of climb).`} />
          {an.back_hours && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Back of the field: the course close, {an.back_hours} h after the start.
            </Typography>
          )}
        </Box>

        {an.bands.length > 0 && (
          <Table size="small" sx={{ maxWidth: 520 }}>
            <TableHead>
              <TableRow>
                <TableCell>Above</TableCell>
                <TableCell align="right">Distance</TableCell>
                <TableCell align="right">Leaders</TableCell>
                <TableCell align="right">Back of field</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {an.bands.map(b => (
                <TableRow key={b.threshold_m}>
                  <TableCell>{b.threshold_m} m</TableCell>
                  <TableCell align="right">{b.distance_km} km</TableCell>
                  <TableCell align="right">{fmtMinutes(b.leader_minutes)}</TableCell>
                  <TableCell align="right">{fmtMinutes(b.back_minutes)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <ElevationProfile profile={an.profile} thresholds={config.routes?.elevation_thresholds_m || []}
                          markers={markers} onToggle={decided ? undefined : toggle} />
      </Box>

      {showMap && (
        <Box sx={{ mt: 2 }}>
          <Suspense fallback={<CircularProgress size={24} />}>
            <SafetyRouteMap routes={allRoutes} focusIds={[route.id]} points={assessment.points} />
          </Suspense>
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ mt: 2 }}>Suggested points</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>#</TableCell>
            <TableCell>Point</TableCell>
            <TableCell align="right">km</TableCell>
            <TableCell align="right">Height</TableCell>
            <TableCell>Leaders – back of field</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {suggestions.map((s, i) => {
            const k = key(route.id, s.distance_km);
            const added = !!onRoute(assessment.points, route.id, s.distance_km);
            return (
              <TableRow key={k}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={added || chosen.has(k)} disabled={added || decided}
                            onChange={() => toggle(k)} slotProps={{ input: { "aria-label": `Choose ${s.name}` } }} />
                </TableCell>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <Tooltip title={s.reason}><span>{s.name}</span></Tooltip>
                  {added && <Chip size="small" label="Added" sx={{ ml: 1 }} />}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{s.reason}</Typography>
                </TableCell>
                <TableCell align="right">{s.distance_km.toFixed(1)}</TableCell>
                <TableCell align="right">{s.elevation_m != null ? `${s.elevation_m} m` : "—"}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {fmtPointWindow(s.leader_at, s.back_at) || "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {!decided && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1.5, alignItems: { sm: "center" } }}>
          <Button variant="contained" size="small" disabled={busy || pending.length === 0}
                  onClick={() => add(pending.map(s => ({ distance_km: s.distance_km })))}>
            Add {pending.length || ""} point{pending.length === 1 ? "" : "s"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Each point costs two Met Office calls per fetch.
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <TextField size="small" label="Add a point at km" type="number" value={manualKm}
                     onChange={(e) => setManualKm(e.target.value)} sx={{ width: 170 }}
                     error={manualKm.trim() !== "" && !manualOk}
                     slotProps={{ htmlInput: { min: 0, max: route.distance_km, step: 0.1 } }} />
          <Button size="small" startIcon={<AddIcon />} disabled={busy || !manualOk}
                  onClick={() => { add([{ distance_km: manual }]); setManualKm(""); }}>
            Add
          </Button>
        </Stack>
      )}
    </Box>
  );
}
